import { AfterContentInit, ChangeDetectionStrategy, Component, ContentChild, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import escapeStringRegexp from 'escape-string-regexp';
import { AppUser } from 'src/app/shared/models/app-user';
import { UserDto } from '../../../services/api.services';
import { isNullOrUndefined, isWhitespaceOnly } from '../../../shared/utils';
import { IItemSelectorCategory, IItemSelectorItem } from '../../state/item-selector/reducer';
import { InsightsLibraryItemSelectorItemActionsDirective } from './item-actions.directive';
import { InsightsLibraryItemSelectorItemDescriptionDirective } from './item-description.directive';
import { InsightsLibraryItemSelectorItemPreviewDirective } from './item-preview.directive';

@Component({
  selector: 'app-insights-library__item-selector',
  templateUrl: './item-selector.component.html',
  styleUrls: ['./item-selector.component.scss'],
  standalone: false
})
export class InsightsLibraryItemSelectorComponent<
  I extends IItemSelectorItem,
  C extends IItemSelectorCategory<I>> implements OnInit, OnChanges, AfterContentInit {
  @Input() user: AppUser;
  @Input() busy = false;

  @Input() descriptionColumnTitle = 'Details';

  @Input() categories: C[] = [];

  @Input() selectedCategoryIndex: number = null;
  @Input() selectedCategory: C;
  @Input() selectedCategoryItems: I[] = [];

  @Input() selectedItemIndex: number = null;
  @Input() selectedItem: I;

  @Output('categorySelected') _categorySelected = new EventEmitter<CategorySelectedEvent<I, C>>();
  @Output('itemSelected') _itemSelected = new EventEmitter<ItemSelectedEvent<I, C>>();

  @Input('itemActions') _itemActions: InsightsLibraryItemSelectorItemActionsDirective;
  @Input('itemDescription') _itemDescription: InsightsLibraryItemSelectorItemDescriptionDirective;
  @Input('itemPreview') _itemPreview: InsightsLibraryItemSelectorItemPreviewDirective;

  @ContentChild(InsightsLibraryItemSelectorItemActionsDirective)
  contentItemActions: InsightsLibraryItemSelectorItemActionsDirective;

  @ContentChild(InsightsLibraryItemSelectorItemDescriptionDirective)
  contentItemDescription: InsightsLibraryItemSelectorItemDescriptionDirective;

  @ContentChild(InsightsLibraryItemSelectorItemPreviewDirective)
  contentItemPreview: InsightsLibraryItemSelectorItemPreviewDirective;

  itemActions: InsightsLibraryItemSelectorItemActionsDirective;
  itemDescription: InsightsLibraryItemSelectorItemDescriptionDirective;
  itemPreview: InsightsLibraryItemSelectorItemPreviewDirective;

  templateContext: ItemSelectorTemplateContext<I, C> = {
    category: null,
    item: null
  }
  
  constructor() {
  }

  ngOnInit() {
    this.updateTemplateContext();

    this.updateDirectives();
  }

  ngOnChanges(changes: SimpleChanges) {
    let updateTemplateContext = false;

    if (changes.categories) {
      updateTemplateContext = true;
    }

    if (changes.selectedCategory) {
      updateTemplateContext = true;
    }

    if (changes.selectedItem) {
      updateTemplateContext = true;
    }

    if (updateTemplateContext) {
      this.updateTemplateContext();
    }

    if (changes.itemActions || changes.itemDescription || changes.itemPreview) {
      this.updateDirectives();
    }
  }

  ngAfterContentInit() {
    this.updateDirectives();
  }

  private updateDirectives() {
    this.itemActions = this._itemActions ?? this.contentItemActions;
    this.itemDescription = this._itemDescription ?? this.contentItemDescription;
    this.itemPreview = this._itemPreview ?? this.contentItemPreview;
  }

  private updateTemplateContext() {
    this.templateContext = {
      category: this.selectedCategory,
      item: this.selectedItem
    };
  }


  categoryTrackBy(index: number, category: C) {
    return category.id;
  }

  itemTrackBy(index: number, item: I) {
    return item.id;
  }


  selectCategory(category: C, index: number) {
    if (category?.id === this.selectedCategory?.id)
      return;

    this._categorySelected.emit({
      component: this,
      category,
      index
    });
  }

  selectItem(item: I, index: number) {
    if (item?.id === this.selectedItem?.id)
      return;

    this._itemSelected.emit({
      component: this,
      item,
      index
    });
  }
}

export interface CategorySelectedEvent<
  I extends IItemSelectorItem,
  C extends IItemSelectorCategory<I>> {
  component: InsightsLibraryItemSelectorComponent<I, C>;
  category: C;
  index: number;
};

export interface ItemSelectedEvent<
  I extends IItemSelectorItem,
  C extends IItemSelectorCategory<I>>  {
  component: InsightsLibraryItemSelectorComponent<I, C>;
  item: I;
  index: number;
};

export interface ItemSelectorTemplateContext<
  I extends IItemSelectorItem,
  C extends IItemSelectorCategory<I>> {
  category: C;
  item: I;
};
